import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { projects } from "../constant/data";
import ProjectModal from "./ProjectModal";
import ImageWithLoader from "./ImageWithLoader";
import { preloadProjectImages } from "../utils/imagePreloader";

const FILTERS = [
  { id: "all", label: "All work" },
  { id: "mobile", label: "Mobile" },
  { id: "web", label: "Web" },
];

function ProjectCard({ project, onClick, index }) {
  const cardRef = useRef(null);
  const innerRef = useRef(null);

  const handleMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    if (innerRef.current) {
      innerRef.current.style.transform = `perspective(1100px) rotateX(${
        -y * 6
      }deg) rotateY(${x * 8}deg) translateZ(0)`;
    }
    card.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    card.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };
  const handleLeave = () => {
    if (innerRef.current) {
      innerRef.current.style.transform =
        "perspective(1100px) rotateX(0deg) rotateY(0deg) translateZ(0)";
    }
  };

  const isMobileApp = project.technologies.some(
    (t) => t === "Flutter" || t === "React Native"
  );
  const cover = project.images ? project.images[0] : project.src;
  // Three cover styles share one 4:3 frame:
  //  - poster: composed 4:3 showcase image, fills the frame exactly
  //  - scrollCrop: tall full-page screenshot, anchored top and pans on hover
  //  - letterbox: wide banner, shown whole over a blurred copy of itself
  const isPoster = !!project.poster;
  const scrollCrop = !isMobileApp && !isPoster;
  const letterbox = !isPoster && !scrollCrop;

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={() => onClick(project)}
      className="group relative cursor-pointer h-full"
    >
      <div
        ref={innerRef}
        className="relative h-full flex flex-col rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] transition-transform duration-300"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-aurora-teal/10 to-aurora-magenta/10">
          {letterbox && (
            <img
              src={cover}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-70"
            />
          )}
          <ImageWithLoader
            src={cover}
            alt={project.alt}
            className={`w-full h-full ${
              letterbox ? "object-contain" : "object-cover"
            } ${
              scrollCrop
                ? "object-top group-hover:[object-position:50%_85%] transition-[object-position] duration-[1500ms] ease-out"
                : ""
            }`}
            priority={index < 4}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/30 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />

          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            {project.technologies.slice(0, 3).map((t) => (
              <span
                key={t}
                className="text-[10px] px-2 py-1 rounded-full bg-ink-950/60 backdrop-blur-sm border border-white/15 text-white/90"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-aurora-teal/20 backdrop-blur-sm border border-aurora-teal/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all translate-y-[-4px] group-hover:translate-y-0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 17 17 7M17 7H8M17 7v9"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        <div className="p-4 md:p-5 flex-1">
          <h3
            className="text-white font-semibold text-base md:text-lg leading-snug line-clamp-1"
            title={project.title}
          >
            {project.title}
          </h3>
          <p className="mt-1 text-xs md:text-sm text-white/55 line-clamp-2">
            {project.description}
          </p>
        </div>

        <div
          className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            background:
              "radial-gradient(420px circle at var(--mx,50%) var(--my,50%), rgba(45,212,191,0.18), transparent 40%)",
          }}
        />
      </div>
    </div>
  );
}

function Showcase({ project, idx, onClick }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.92, 1, 1.06]);

  const isMobileApp = project.technologies.some(
    (t) => t === "Flutter" || t === "React Native"
  );
  // Featured items stack vertically, so each frame can match its image:
  // 4:3 for composed posters, 2:1 for wide banners and tall page captures.
  const isPoster = !!project.poster;
  const scrollCrop = !isMobileApp && !isPoster;
  const reverse = idx % 2 === 1;

  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
        reverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
    >
      {/* Image */}
      <motion.div
        style={{ y, scale }}
        initial={{ opacity: 0, x: reverse ? 90 : -90 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        onClick={() => onClick(project)}
        className="relative cursor-pointer group"
      >
        <div className="absolute -inset-4 bg-gradient-to-br from-aurora-teal/30 via-aurora-magenta/20 to-aurora-amber/20 rounded-3xl blur-3xl opacity-60 group-hover:opacity-90 transition-opacity" />
        <div
          className={`relative rounded-3xl overflow-hidden border border-white/10 ${
            isPoster ? "aspect-[4/3]" : "aspect-[2/1]"
          } bg-gradient-to-br from-ink-800 to-ink-900`}
        >
          <ImageWithLoader
            src={project.images ? project.images[0] : project.src}
            alt={project.alt}
            className={`w-full h-full object-cover ${
              scrollCrop
                ? "object-top group-hover:[object-position:50%_85%] transition-[object-position] duration-[1500ms] ease-out"
                : ""
            }`}
            priority={idx < 2}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950/40 to-transparent" />
        </div>
      </motion.div>

      {/* Copy */}
      <div>
        <motion.div
          initial={{ opacity: 0, x: reverse ? -80 : 80 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        >
          <div className="text-[11px] uppercase tracking-[0.32em] text-aurora-teal mb-3">
            Featured · {String(idx + 1).padStart(2, "0")}
          </div>
          <h3 className="font-jamjuree text-3xl md:text-4xl lg:text-5xl font-semibold text-white leading-tight mb-5">
            {project.title}
          </h3>
          <p className="text-sm md:text-base text-white/65 leading-relaxed max-w-xl">
            {project.description}
          </p>

          <div className="flex flex-wrap gap-2 mt-6">
            {project.technologies.map((t) => (
              <span
                key={t}
                className="text-[11px] px-3 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/80"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => onClick(project)}
              className="btn-primary !py-2.5 !px-5"
            >
              View case
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {project.liveLink &&
            project.liveLink.trim() !== "" &&
            project.liveLink.trim() !== "https://apps.apple.com/app" &&
            project.liveLink.trim() !== "https://play.google.com/store/apps" ? (
              <a
                href={project.liveLink}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost !py-2.5 !px-5"
              >
                Live link
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M7 17 17 7M17 7H8M17 7v9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            ) : project.private ? null : (
              <span
                className="btn-ghost !py-2.5 !px-5 opacity-50 cursor-not-allowed"
                aria-disabled="true"
              >
                Coming soon
              </span>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

const Section3 = ({ featuredOnly = false }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    // Warm only the featured covers; grid images lazy-load as they scroll in.
    preloadProjectImages(projects.slice(0, 3)).catch((err) =>
      console.warn("Failed to preload project images:", err)
    );
  }, []);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const filteredProjects = projects.filter((project) => {
    if (filter === "all") return true;
    if (filter === "mobile")
      return project.technologies.some(
        (t) => t === "Flutter" || t === "React Native"
      );
    if (filter === "web")
      return !project.technologies.some(
        (t) => t === "Flutter" || t === "React Native"
      );
    return true;
  });

  const featured = projects.slice(0, 3);

  return (
    <section
      id="portfolio"
      className="relative w-full overflow-hidden"
    >
      {/* Heading */}
      <div className="relative w-full px-6 md:px-12 lg:px-24 pt-14 md:pt-20 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-4 md:mb-6">
            <span className="eyebrow">
              <span className="dot" />
              Selected work
            </span>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 font-jamjuree text-3xl md:text-5xl lg:text-6xl font-semibold text-white leading-tight"
            >
              Projects that <span className="text-gradient">ship</span>.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-5 text-sm md:text-base text-white/65 max-w-2xl mx-auto leading-relaxed"
            >
              A selection of products my team and I have designed, built and
              shipped — from consumer mobile apps to e‑commerce platforms and
              beyond.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Featured showcases */}
      <div className="relative w-full px-6 md:px-12 lg:px-24 py-8 md:py-10">
        <div className="max-w-7xl mx-auto flex flex-col gap-20 md:gap-28">
          {featured.map((p, i) => (
            <Showcase
              key={p.id}
              project={p}
              idx={i}
              onClick={handleProjectClick}
            />
          ))}
        </div>
      </div>

      {featuredOnly && (
        <div className="relative w-full px-6 md:px-12 lg:px-24 pt-8 pb-6">
          <div className="max-w-7xl mx-auto flex justify-center">
            <Link to="/portfolio" className="btn-ghost !py-3 !px-6">
              View all {projects.length} projects
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </div>
      )}

      {/* Filter + grid */}
      {!featuredOnly && (
      <div className="relative w-full px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <span className="eyebrow">
                <span className="dot" />
                Browse all
              </span>
              <h3 className="mt-4 font-jamjuree text-2xl md:text-3xl font-semibold text-white">
                Every project, in one place
              </h3>
            </div>

            <div className="inline-flex p-1 rounded-full glass border border-white/10 self-start md:self-end">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`relative px-4 md:px-5 py-2 text-xs md:text-sm font-medium rounded-full transition-colors ${
                    filter === f.id
                      ? "text-ink-950"
                      : "text-white/55 hover:text-white"
                  }`}
                >
                  {filter === f.id && (
                    <motion.div
                      layoutId="filter-pill"
                      className="absolute inset-0 rounded-full"
                      style={{
                        background:
                          "linear-gradient(120deg, #2dd4bf, #5eead4)",
                        boxShadow:
                          "0 8px 24px -8px rgba(45,212,191,0.6)",
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 32,
                      }}
                    />
                  )}
                  <span className="relative">{f.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filteredProjects.map((project, index) => {
              const col = index % 3;
              const fromX = col === 0 ? -60 : col === 2 ? 60 : 0;
              const fromY = col === 1 ? 40 : 0;
              return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, x: fromX, y: fromY }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.75,
                  delay: (Math.floor(index / 3) % 3) * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <ProjectCard
                  project={project}
                  index={index}
                  onClick={handleProjectClick}
                />
              </motion.div>
              );
            })}
          </div>

          <div className="mt-20 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-center">
            {[
              "Skilled experts",
              "Creativity",
              "Magical world",
              "Crafted with care",
            ].map((w, i) => (
              <React.Fragment key={w}>
                <span
                  className="text-xl md:text-3xl lg:text-5xl font-jamjuree tracking-tight"
                  style={{
                    color: "transparent",
                    WebkitTextStroke: "1px rgba(255,255,255,0.22)",
                  }}
                >
                  {w}
                </span>
                {i < 3 && (
                  <span className="text-aurora-teal/70 text-xs">◆</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      )}

      <ProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        project={selectedProject}
      />
    </section>
  );
};

export default Section3;
